import { useCallback, useEffect, useMemo } from 'react';

import { CirclePlusIcon } from 'lucide-react';
import { Controller, FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Accordion from '@/components/atoms/Accordion/Accordion';
import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import fs from '@/components/atoms/Fieldset';
import OptionRadioButton from '@/components/atoms/OptionRadioButton/OptionRadioButton';
import TitleSubtitle from '@/components/atoms/TitleSubtitle/TitleSubtitle';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { useIntegrations } from '@/hooks/useIntegrations';
import usePrompt from '@/hooks/usePrompt';
import { cn } from '@/lib/utils';
import { setDirty, setExpanded } from '@/redux/integrations/actions';
import { selectAccordion } from '@/redux/integrations/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import {
  AccordionFieldsetProps,
  PageTriggers,
  WebWidgetIntegration,
} from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { DEFAULT_START_MESSAGE } from '@/util/constants';
import { errorMessage, trimOnBlur } from '@/util/validation/helpers';
import {
  integrationRulesWebWidget,
  welcomeTriggerMessageValidator,
} from '@/util/validation/rules/integration-rules';

import { parsePageList, toPageListText } from './PageRules/page-rules';
import PageRules, { PageRulesHostForm } from './PageRules/PageRules';
import RegexCard from './RegexCard/RegexCard';

import styles from './StartBehavior.module.scss';

type AdvancedOption = { slug: string; trigger_message: string; opening_message: string };

type Form = {
  options: AdvancedOption[];
  advanced_messages_checkbox: boolean;
  welcome_trigger_message: string;
  welcome_screen_greeting: string;
  starters: string[];
  start_behavior_option: string;
} & PageRulesHostForm;
const StartBehavior = ({
  type,
  integration,
  toggleAccordion,
  registerAccordion,
}: AccordionFieldsetProps<Form, WebWidgetIntegration>) => {
  const { t } = useTranslation();
  const { expanded } = useSelector(selectAccordion);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );
  const { updateIntegration, updateStatus } = useIntegrations<WebWidgetIntegration>(
    integration?.desk_id,
    integration?.integration_id
  );
  const welcomeTriggerMessage = integration?.config?.welcome_trigger_message;
  // ensure backward compatibility for users that already have a default start message
  const isWelcomeTriggerMessageLocked =
    !canWrite || welcomeTriggerMessage === DEFAULT_START_MESSAGE;
  const dispatch = useDispatch();

  const startBehaviorOptions = useMemo(
    () => [
      { id: 0, value: 'user_input', label: t('integrations.web_start.user') },
      { id: 1, value: 'auto_start', label: t('integrations.web_start.auto') },
      { id: 2, value: 'welcome_screen', label: t('integrations.web_start.welcome') },
    ],
    [t]
  );

  const prepareTriggers = (
    options: { opening_message: string; trigger_message: string; slug: string }[]
  ): PageTriggers => {
    if (!options || options.length === 0) {
      return;
    }
    const newPageTriggers = {};
    options.forEach(
      (option) =>
        (newPageTriggers[`${option.slug?.trim()}`] = {
          opening_message: option.opening_message?.trim(),
          trigger_message: option.trigger_message?.trim(),
        })
    );
    return newPageTriggers;
  };

  const revertTriggers = (resp) => {
    if (!resp) {
      return;
    }
    const objectKeys = Object.keys(resp);
    return objectKeys.map((key) => ({
      slug: key,
      trigger_message: resp[key].trigger_message,
      opening_message: resp[key].opening_message,
    }));
  };

  const formMethods = useForm<Form>({ mode: 'onSubmit' });

  const {
    reset,
    formState: { errors, isDirty },
    control,
    handleSubmit,
    trigger,
    getValues,
  } = formMethods;

  const [advancedMessages, selectedOption] = useWatch({
    control,
    name: ['advanced_messages_checkbox', 'start_behavior_option'],
  });

  const welcomeTriggerMessageErrorMessage = errorMessage({
    field: errors.welcome_trigger_message,
    maxLength: welcomeTriggerMessageValidator.maxLength,
  });

  const starterRules = {
    maxLength: { value: 64, message: t('integrations.web_start.starters.max_chars', { 0: 64 }) },
  };

  const starterLabels = [
    t('integrations.web_start.starters.first'),
    t('integrations.web_start.starters.second'),
    t('integrations.web_start.starters.third'),
  ];

  const welcomeScreenGreetingErrorMessage = errorMessage({
    field: errors.welcome_screen_greeting,
    maxLength: integrationRulesWebWidget.welcomeTriggerMessage.maxLength,
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'options' });

  useEffect(() => {
    const saved = integration?.config?.welcome_screen?.starters;
    reset({
      welcome_trigger_message:
        integration?.config?.welcome_trigger_message || DEFAULT_START_MESSAGE,
      welcome_screen_greeting: integration?.config?.welcome_screen?.greeting || '',
      starters: [saved?.[0] ?? '', saved?.[1] ?? '', saved?.[2] ?? ''],
      options: revertTriggers(integration?.config?.page_triggers) || [],
      advanced_messages_checkbox: !!integration?.config?.page_triggers,
      show_on_pages: toPageListText(integration?.config?.show_on_pages),
      hide_on_pages: toPageListText(integration?.config?.hide_on_pages),
      start_behavior_option: integration?.config?.welcome_trigger_message
        ? 'auto_start'
        : integration?.config?.welcome_screen?.enabled
          ? 'welcome_screen'
          : 'user_input',
    });
  }, [
    integration?.config?.hide_on_pages,
    integration?.config?.page_triggers,
    integration?.config?.show_on_pages,
    integration?.config?.welcome_screen?.enabled,
    integration?.config?.welcome_screen?.greeting,
    integration?.config?.welcome_screen?.starters,
    integration?.config?.welcome_trigger_message,
    reset,
  ]);

  const handleUpdateIntegration = useCallback(() => {
    const {
      welcome_screen_greeting,
      starters,
      options,
      advanced_messages_checkbox,
      start_behavior_option,
      show_on_pages,
      hide_on_pages,
    } = getValues();
    const newOptions = prepareTriggers(options);
    const nonEmptyStarters = starters?.filter((x) => !!x);
    updateIntegration(
      {
        ...integration,
        config: {
          ...integration.config,
          // Both keys on every save: the orchestrator keeps a stored list when a save omits
          // its key, so an emptied list has to reach the wire as `[]` to clear it.
          show_on_pages: parsePageList(show_on_pages),
          hide_on_pages: parsePageList(hide_on_pages),
          welcome_trigger_message:
            start_behavior_option === 'auto_start' ? DEFAULT_START_MESSAGE : undefined,
          page_triggers:
            advanced_messages_checkbox && start_behavior_option === 'auto_start'
              ? newOptions
              : undefined,
          welcome_screen:
            start_behavior_option === 'welcome_screen'
              ? {
                  enabled: start_behavior_option === 'welcome_screen',
                  greeting: welcome_screen_greeting,
                  starters: nonEmptyStarters?.length ? nonEmptyStarters : undefined,
                }
              : undefined,
        },
      },
      {
        onSuccess: () => {
          if (expanded === type) {
            dispatch(setExpanded(false));
          }
        },
      }
    );
  }, [dispatch, expanded, getValues, integration, type, updateIntegration]);

  usePrompt(isDirty && canWrite, undefined, undefined, handleSubmit(handleUpdateIntegration));

  const handleAddOption = useCallback(() => {
    if (canWrite) {
      append({ slug: '', trigger_message: '', opening_message: '' });
    }
  }, [append, canWrite]);

  const handleDelete = useCallback(
    (index) => {
      remove(index);
    },
    [remove]
  );

  useEffect(() => {
    dispatch(setDirty(isDirty));
  }, [dispatch, isDirty]);

  // for reseting changes
  useEffect(() => {
    if (expanded !== type && isDirty) {
      reset();
    }
  }, [dispatch, expanded, isDirty, reset, type]);

  return (
    // `PageRules` reads the form from context rather than having `control`
    // prop-drilled the way `RegexCard` does.
    <FormProvider {...formMethods}>
      <Accordion
        title={t('integrations.web_start.title')}
        subtitle={t('integrations.web_start.subtitle')}
        footerText={t('integrations.web_start.footer')}
        onSubmit={handleSubmit(handleUpdateIntegration)}
        disabled={!isDirty}
        isLoading={updateStatus === 'pending'}
        expanded={expanded === type}
        handleChange={toggleAccordion(type)}
        readOnly={!canWrite}
        ref={registerAccordion(type, handleSubmit(handleUpdateIntegration), trigger)}
      >
        <>
          <TitleSubtitle
            title={t('integrations.web_start.title2')}
            subtitle={t('integrations.web_start.subtitle2')}
          />
          <div className={cn(styles.radioWrapper, { [styles.disabled]: !canWrite })}>
            <Controller
              render={({ field: { onChange, value } }) => {
                return (
                  <>
                    {startBehaviorOptions.map((option) => {
                      return (
                        <OptionRadioButton
                          option={option}
                          key={option.id}
                          isSelected={option.value === value}
                          disabled={!canWrite}
                          onChange={onChange}
                        />
                      );
                    })}
                  </>
                );
              }}
              name="start_behavior_option"
              control={control}
            />
          </div>
          {selectedOption === 'user_input' && (
            <TitleSubtitle subtitle={t('integrations.web_start.user_note')} />
          )}

          {selectedOption === 'auto_start' && (
            <>
              <ContextualHelp
                title={t('integrations.web_start.help_title')}
                name={t('integrations.web_start.help_name')}
              >
                {t('integrations.web_start.help_body')}
              </ContextualHelp>

              <div className={cn(styles.textArea, 'mb-6')}>
                <div className="w-full flex-1">
                  <Label
                    htmlFor="welcome_trigger_message"
                    className={cn('mb-2', !!errors.welcome_trigger_message && 'text-warning')}
                  >
                    {t('integrations.web_start.trigger')}
                  </Label>
                  <Controller
                    control={control}
                    name="welcome_trigger_message"
                    rules={{
                      required:
                        selectedOption === 'auto_start' ? t('common.required_field') : undefined,
                      maxLength: { value: 64, message: '' },
                    }}
                    render={({ field }) => (
                      <Input
                        id="welcome_trigger_message"
                        placeholder={t('integrations.web_start.trigger_placeholder')}
                        aria-invalid={!!errors.welcome_trigger_message}
                        readOnly={isWelcomeTriggerMessageLocked}
                        className={cn(
                          isWelcomeTriggerMessageLocked &&
                            'bg-bg-muted-50 text-foreground-muted focus-visible:ring-0 focus-visible:ring-offset-0'
                        )}
                        {...field}
                        value={field.value ?? ''}
                        onBlur={trimOnBlur(field)}
                      />
                    )}
                  />
                  {welcomeTriggerMessageErrorMessage && (
                    <p className="mt-1 text-sm text-warning">{welcomeTriggerMessageErrorMessage}</p>
                  )}
                </div>
                {welcomeTriggerMessage === DEFAULT_START_MESSAGE && (
                  <CopyButton data={DEFAULT_START_MESSAGE} />
                )}
              </div>

              <div className={styles.advanced}>
                <Controller
                  render={({ field: { onChange, value } }) => {
                    return (
                      <fs.Checkbox
                        label={
                          <TitleSubtitle
                            title={t('integrations.web_start.advance_title2')}
                            subtitle={t('integrations.web_start.welcome_placeholder')}
                            noGutters
                          />
                        }
                        onChange={onChange}
                        checked={!!value}
                        sameLevel
                        disabled={!canWrite}
                      />
                    );
                  }}
                  name="advanced_messages_checkbox"
                  control={control}
                />
              </div>

              {advancedMessages && (
                <>
                  <div
                    className={styles.interactive}
                    role="button"
                    onClick={handleAddOption}
                    onKeyDown={() => {}}
                    tabIndex={0}
                  >
                    <span>
                      <CirclePlusIcon size={16} className="text-foreground-muted" />
                    </span>
                    {t('integrations.web_start.add_msg')}
                  </div>
                  <div>
                    {fields.map((option, index) => (
                      <RegexCard
                        key={option.id}
                        index={index}
                        control={control}
                        readOnly={!canWrite}
                        onDelete={() => handleDelete(index)}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {selectedOption === 'welcome_screen' && (
            <>
              <div className="pb-3">
                <Label
                  htmlFor="greeting"
                  className={cn('mb-2', !!errors.welcome_screen_greeting && 'text-warning')}
                >
                  {t('integrations.web_start.headline')}
                </Label>
                <Controller
                  control={control}
                  name="welcome_screen_greeting"
                  rules={{
                    required:
                      selectedOption === 'welcome_screen' ? t('common.required_field') : undefined,
                    maxLength: { value: 64, message: '' },
                  }}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      value={field.value ?? ''}
                      onBlur={trimOnBlur(field)}
                      id="greeting"
                      placeholder={t('integrations.web_start.gretting_placeholder')}
                      aria-invalid={!!errors.welcome_screen_greeting}
                      readOnly={!canWrite}
                      minRows={3}
                      maxRows={10}
                      className={cn(
                        !canWrite &&
                          'bg-bg-muted-50 text-foreground-muted focus-visible:ring-0 focus-visible:ring-offset-0'
                      )}
                    />
                  )}
                />
                {welcomeScreenGreetingErrorMessage && (
                  <p className="mt-1 text-sm text-warning">{welcomeScreenGreetingErrorMessage}</p>
                )}
              </div>

              <div className={styles.requiredField}>
                {t('integrations.web_start.gretting_help')}
              </div>

              <TitleSubtitle
                title={t('integrations.web_start.starters.title')}
                subtitle={t('integrations.web_start.starters.subtitle')}
              />
              {starterLabels.map((label, index) => (
                <Controller
                  key={label}
                  control={control}
                  name={`starters.${index}`}
                  rules={starterRules}
                  render={({ field, fieldState: { error } }) => (
                    <div className={styles.starter}>
                      <Label
                        htmlFor={`starters-${index}`}
                        className={cn('mb-2', !!error && 'text-warning')}
                      >
                        {label}
                      </Label>
                      <Input
                        id={`starters-${index}`}
                        placeholder={t('integrations.web_start.starters.type_question')}
                        aria-invalid={!!error}
                        readOnly={!canWrite}
                        {...field}
                        value={field.value ?? ''}
                        onBlur={trimOnBlur(field)}
                      />
                      {error?.message && (
                        <p className="mt-1 text-sm text-warning">{error.message}</p>
                      )}
                    </div>
                  )}
                />
              ))}
            </>
          )}

          <Separator className="my-6" />
          <PageRules readOnly={!canWrite} />
        </>
      </Accordion>
    </FormProvider>
  );
};

export default StartBehavior;
